import React, { useEffect, useState } from "react";
import { Link, useLocation, useRawNavigate } from "@/i18n/router";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ArrowUpRight, LayoutGrid, Globe, Search } from "lucide-react";
import { Logo } from "@/components/Logo";
import { nav } from "@/data/site";
import { ecosystem } from "@/data/institutional";
import { useLang } from "@/i18n/LanguageContext";
import { LANGUAGES } from "@/i18n/dictionaries";
import { swapLocale } from "@/i18n/locale";
import { PLATFORM_URL } from "@/lib/api";

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSub, setOpenSub] = useState(null);
  const location = useLocation();
  const { lang, setLang, t, tr } = useLang();
  const rawNavigate = useRawNavigate();
  const switchLang = (code) => {
    setLang(code);
    rawNavigate(swapLocale(location.pathname, code) + location.search);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenSub(null);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "backdrop-blur-xl bg-navy/80 border-b border-navy-border" : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <nav className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex h-[72px] items-center justify-between">
          <Logo />

          <ul className="hidden lg:flex items-center gap-1">
            {nav.map((item) => (
              <li key={item.to} className="relative group">
                <Link
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`inline-flex items-center gap-1 rounded-full px-3.5 py-2 text-sm font-medium tracking-wide transition-colors ${
                    item.highlight
                      ? "text-astro-light hover:text-white"
                      : "text-silver/80 hover:text-white"
                  }`}
                >
                  {tr(item.label)}
                  {item.children && <ChevronDown className="h-3.5 w-3.5 opacity-60" />}
                </Link>
                {item.children && (
                  <div className="invisible absolute left-0 top-full pt-3 opacity-0 translate-y-1 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0">
                    <div className="w-56 rounded-xl border border-navy-border bg-navy-surface p-2 shadow-2xl">
                      {item.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          className="block rounded-lg px-3 py-2 text-sm text-silver/80 hover:bg-navy-soft hover:text-white transition-colors"
                        >
                          {tr(c.label)}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>

          <div className="hidden lg:flex items-center gap-2">
            <Link to="/search" data-testid="nav-search" aria-label="Search" className="inline-flex h-9 w-9 items-center justify-center rounded-full text-silver/80 hover:text-white hover:bg-white/10 transition-colors">
              <Search className="h-4 w-4" />
            </Link>
            {/* Ecosystem launcher */}
            <div className="relative group">
              <button
                data-testid="nav-ecosystem"
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium text-silver/80 hover:text-white transition-colors"
                aria-label="AstroLife ecosystem"
              >
                <LayoutGrid className="h-4 w-4" /> {t("nav.ecosystem")}
              </button>
              <div className="invisible absolute right-0 top-full pt-3 opacity-0 translate-y-1 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0">
                <div className="w-64 rounded-xl border border-navy-border bg-navy-surface p-2 shadow-2xl">
                  {ecosystem.map((e) =>
                    e.href ? (
                      <span key={e.label} className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm text-silver/40">
                        <span className="flex items-center gap-2"><e.icon className="h-4 w-4" /> {tr(e.label)}</span>
                        {e.soon && <span className="text-[10px] uppercase tracking-wider text-astro-light/70">Soon</span>}
                      </span>
                    ) : (
                      <Link key={e.label} to={e.to} className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-silver/80 hover:bg-navy-soft hover:text-white transition-colors">
                        <e.icon className="h-4 w-4 text-astro-light" /> {tr(e.label)}
                      </Link>
                    ),
                  )}
                </div>
              </div>
            </div>

            {/* Language switcher */}
            <div className="flex items-center gap-0.5 rounded-full border border-navy-border p-0.5">
              {LANGUAGES.filter((l) => !l.disabled).map((l) => (
                <button
                  key={l.code}
                  onClick={() => switchLang(l.code)}
                  data-testid={`lang-${l.code}`}
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${lang === l.code ? "bg-astro-blue text-white" : "text-silver/60 hover:text-white"}`}
                >
                  {l.short}
                </button>
              ))}
            </div>

            </div>

          <button
           className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg text-white"
           onClick={() => setMobileOpen((v) => !v)}
           aria-label="Toggle menu"
           data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden border-t border-navy-border bg-navy/95 backdrop-blur-xl"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-4 space-y-1">
              {nav.map((item) => (
                <div key={item.to}>
                  <div className="flex items-center justify-between">
                    <Link to={item.to} className="block flex-1 py-2.5 text-base font-medium text-silver">
                      {tr(item.label)}
                    </Link>
                    {item.children && (
                      <button
                        onClick={() => setOpenSub(openSub === item.to ? null : item.to)}
                        className="p-2 text-silver"
                        aria-label="Expand"
                      >
                        <ChevronDown className={`h-4 w-4 transition-transform ${openSub === item.to ? "rotate-180" : ""}`} />
                      </button>
                    )}
                  </div>
                  {item.children && openSub === item.to && (
                    <div className="ml-3 border-l border-navy-border pl-3 pb-2">
                      {item.children.map((c) => (
                        <Link key={c.to} to={c.to} className="block py-2 text-sm text-silver/70">
                          {tr(c.label)}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="mt-3 flex items-center gap-2 border-t border-navy-border pt-3">
                <Globe className="h-4 w-4 text-silver/60" />
                {LANGUAGES.filter((l) => !l.disabled).map((l) => (
                  <button
                    key={l.code}
                    onClick={() => switchLang(l.code)}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold ${lang === l.code ? "bg-astro-blue text-white" : "border border-navy-border text-silver/60"}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
              <a
                href={PLATFORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 flex items-center justify-center gap-1.5 rounded-full bg-astro-blue px-5 py-3 text-sm font-semibold text-white"
              >
                {t("cta.access")} <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
